import React from "react";
import {
  Box,
  CircularProgress,
  createStyles,
  Grid,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

import CardCommon from "../../../../components/card/CardCommon";
import { CustomTheme } from "../../../../types/customTheme";
import { formatDuration, formatPercentage } from "./historicalUtils";

/**
 * INSIGHT-HIST-12 — the summary tiles.
 *
 * Every tile is the same height whatever it holds, so a tile with a note under
 * it does not sit taller than the rest.
 *
 * "No Data" and zero are shown differently on purpose: an empty selection says
 * No Data, and a real zero prints as a zero.
 */

const TILE_HEIGHT = 140;

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    tile: {
      height: TILE_HEIGHT,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: "20px",
    },
    label: {
      color: theme.palette.custom.orange.contrastText,
    },
    note: {
      color: theme.palette.custom.orange.contrastText,
      opacity: 0.7,
      marginTop: "4px",
    },
    value: {
      marginTop: "6px",
    },
  }),
);

export type HistoricalSummary = {
  hasData: boolean;
  completed?: number;
  avgDurationMs?: number | null;
  medianDurationMs?: number | null;
  longestDurationMs?: number | null;
  withinTargetCount?: number;
  withinTargetPercentage?: number | null;
  targetCoverage?: number;
};

export interface HistoricalSummaryCardsProps {
  summary: HistoricalSummary | null;
  isLoading: boolean;
  error?: string;
}

const HistoricalSummaryCards: React.FunctionComponent<HistoricalSummaryCardsProps> = ({
  summary,
  isLoading,
  error,
}) => {
  const classes = useStyles();

  const Tile: React.FunctionComponent<{
    label: string;
    value: string;
    note?: string;
  }> = ({ label, value, note }) => (
    <Grid item xs={12} sm={6} md={3}>
      <CardCommon backgroundColor="entity_background">
        <Box className={classes.tile}>
          <Typography variant="body2" align="center" className={classes.label}>
            {label}
          </Typography>
          <Typography variant="h4" align="center" className={classes.value}>
            {value}
          </Typography>
          <Typography variant="caption" align="center" className={classes.note}>
            {note || " "}
          </Typography>
        </Box>
      </CardCommon>
    </Grid>
  );

  if (isLoading) {
    return (
      <CardCommon backgroundColor="entity_background">
        <Box
          height={TILE_HEIGHT}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={28} />
        </Box>
      </CardCommon>
    );
  }

  // An error is not the same as an empty result, so it says so.
  if (error) {
    return (
      <CardCommon backgroundColor="entity_background">
        <Box
          height={TILE_HEIGHT}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography color="error">{error}</Typography>
        </Box>
      </CardCommon>
    );
  }

  if (!summary || !summary.hasData) {
    return (
      <CardCommon backgroundColor="entity_background">
        <Box
          height={TILE_HEIGHT}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="h5">No Data</Typography>
          <Typography variant="caption" className={classes.note}>
            No completed metrics for the selected dates and filters.
          </Typography>
        </Box>
      </CardCommon>
    );
  }

  const completed = summary.completed || 0;
  const coverage = summary.targetCoverage || 0;

  // When only some metrics had a target, the percentage rests on partial
  // information and the tile says so rather than hiding it.
  const targetNote =
    coverage === 0
      ? "No targets known"
      : coverage < completed
      ? `Based on ${coverage} of ${completed} with a target`
      : undefined;

  return (
    <Grid container spacing={2}>
      <Tile
        label="Average"
        value={formatDuration(summary.avgDurationMs)}
        note={`${completed} completed`}
      />
      <Tile label="Median" value={formatDuration(summary.medianDurationMs)} />
      <Tile label="Longest" value={formatDuration(summary.longestDurationMs)} />
      <Tile
        label="Within target"
        value={
          coverage === 0
            ? "No Data"
            : formatPercentage(summary.withinTargetPercentage)
        }
        note={targetNote}
      />
    </Grid>
  );
};

export default HistoricalSummaryCards;
